/**
 * activityTracker — يُرسل أحداث الزائر إلى /api/public/track-event.
 *
 * - session_id يُحفظ في localStorage لمدّة ٣٠ يوم
 * - page_view عند كل تنقّل (يُستدعى من __root)
 * - heartbeat كل ١٢٠ ثانية للحفاظ على live_sessions (يتوقّف عند خفاء التبويب)
 * - sendBeacon للأمان عند إغلاق التبويب
 *
 * helpers تُستدعى من المواقع الموجودة:
 *   trackAddToCart, trackWishlist, trackLogin, trackBeginCheckout, trackAgentOpen
 */
import { ACTIVITY_HEARTBEAT_MS as HEARTBEAT_MS } from "./activityTracking.config";
import { analyticsUrl } from "./analyticsUrl";

const STORAGE_KEY = "lecmo_session_id";
const VISITOR_KEY = "lecmo_visitor_id";
const STORAGE_TS_KEY = "lecmo_session_ts";
const SESSION_TTL_DAYS = 30;

let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
let trackerInited = false;
let lastPath: string | null = null;
// Storage can be unavailable in embedded browsers or throw on writes. Keep one
// identity for this document so navigation and cart events stay linked.
let memorySession: { id: string; createdAt: number } | null = null;
let memoryVisitorId: string | null = null;

function genId(): string {
  // 16 bytes hex → 32 chars
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

export function getSessionId(): string {
  if (typeof window === "undefined") return "";
  try {
    const id = localStorage.getItem(STORAGE_KEY);
    const storedTs = Number(localStorage.getItem(STORAGE_TS_KEY) ?? 0);
    // A quota error can happen after the ID write but before its timestamp.
    const ts = storedTs || (memorySession && id === memorySession.id ? memorySession.createdAt : 0);
    const ageDays = (Date.now() - ts) / 86_400_000;
    if (id && ageDays < SESSION_TTL_DAYS) {
      memorySession = { id, createdAt: ts };
      return id;
    }
    // An expired persisted ID may be impossible to overwrite. Reuse its valid
    // in-memory replacement instead of generating a new ID for every event.
    if (!memorySession || (Date.now() - memorySession.createdAt) / 86_400_000 >= SESSION_TTL_DAYS) {
      memorySession = { id: genId(), createdAt: Date.now() };
    }
    localStorage.setItem(STORAGE_KEY, memorySession.id);
    localStorage.setItem(STORAGE_TS_KEY, String(memorySession.createdAt));
    return memorySession.id;
  } catch {
    if (!memorySession || (Date.now() - memorySession.createdAt) / 86_400_000 >= SESSION_TTL_DAYS) {
      memorySession = { id: genId(), createdAt: Date.now() };
    }
    return memorySession.id;
  }
}

/**
 * معرّف زائر دائم (بلا انتهاء) — يربط الجلسات المجهولة بالسلّة والشراء.
 * يُرسل مع كل حدث تتبّع، ويُمرَّر كسمة `_visitor_id` إلى سلّة Shopify.
 */
export function getVisitorId(): string {
  if (typeof window === "undefined") return "";
  try {
    const existing = localStorage.getItem(VISITOR_KEY);
    if (existing) {
      memoryVisitorId = existing;
      return existing;
    }
    memoryVisitorId ??= genId();
    localStorage.setItem(VISITOR_KEY, memoryVisitorId);
    return memoryVisitorId;
  } catch {
    return (memoryVisitorId ??= genId());
  }
}

/** سمات السلّة الخاصّة بمعرّف الزائر. */
export function buildVisitorAttributes(): Array<{ key: string; value: string }> {
  const id = getVisitorId();
  return id ? [{ key: "_visitor_id", value: id }] : [];
}

function getDevice(): string {
  if (typeof window === "undefined") return "unknown";
  const w = window.innerWidth;
  if (w < 640) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

/** يقرأ حالة اللغة/السوق المخزَّنة من LocaleContext (`lux-locale-v3`). */
function readLocaleState(): { market?: string; locale?: string } {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem("lux-locale-v3");
    if (!raw) return {};
    const parsed = JSON.parse(raw) as { market?: unknown; locale?: unknown };
    return {
      market: typeof parsed.market === "string" ? parsed.market : undefined,
      locale: typeof parsed.locale === "string" ? parsed.locale : undefined,
    };
  } catch {
    return {};
  }
}

function getMarket(): string | undefined {
  return readLocaleState().market;
}

function getLocale(): string | undefined {
  if (typeof window === "undefined") return undefined;
  return readLocaleState().locale ?? document.documentElement.lang ?? undefined;
}


// ⚠️ ممنوع نداء `supabase.auth.getSession()` هنا — يستحوذ على navigatorLock
// ويتنافس مع AuthContext. نستعمل snapshot مشترك بلا قفل (راجع auth-snapshot.ts).
async function getAuthHeader(): Promise<Record<string, string>> {
  try {
    const { getAuthSnapshot } = await import("@/lib/auth-snapshot");
    const tok = getAuthSnapshot().accessToken;
    return tok ? { authorization: `Bearer ${tok}` } : {};
  } catch {
    return {};
  }
}

async function send(payload: Record<string, unknown>, useBeacon = false): Promise<void> {
  if (typeof window === "undefined") return;
  const body = JSON.stringify({
    session_id: getSessionId(),
    visitor_id: getVisitorId() || undefined,
    market: getMarket(),
    locale: getLocale(),
    device: getDevice(),
    referrer: document.referrer ? analyticsUrl(document.referrer) : undefined,
    ...payload,
  });

  const url = "/api/public/track-event";

  // Beacon path — used on pagehide/unload. Cannot set custom headers, so user_id
  // attribution will be lost for that one event; acceptable trade-off.
  if (useBeacon && navigator.sendBeacon) {
    try {
      const blob = new Blob([body], { type: "application/json" });
      if (navigator.sendBeacon(url, blob)) return;
    } catch {
      /* fallthrough */
    }
  }

  try {
    const headers: Record<string, string> = { "content-type": "application/json" };
    Object.assign(headers, await getAuthHeader());
    await fetch(url, { method: "POST", headers, body, keepalive: true });
  } catch {
    /* swallow */
  }
}

// ───────────────────────── Public API ─────────────────────────

export function trackPageView(path: string): void {
  path = analyticsUrl(path);
  if (path === lastPath) return;
  lastPath = path;
  void send({ event_type: "page_view", path });
}

export function trackHeartbeat(): void {
  void send({ event_type: "page_view", path: lastPath ?? "/", heartbeat: true });
}

export function trackAddToCart(handle?: string, title?: string): void {
  void send({
    event_type: "add_to_cart",
    path: lastPath ?? undefined,
    product_handle: handle,
    product_title: title,
  });
}

export function trackRemoveFromCart(handle?: string, title?: string): void {
  void send({
    event_type: "remove_from_cart",
    path: lastPath ?? undefined,
    product_handle: handle,
    product_title: title,
  });
}



export function trackWishlist(action: "add" | "remove", handle?: string, title?: string): void {
  void send({
    event_type: action === "add" ? "wishlist_add" : "wishlist_remove",
    path: lastPath ?? undefined,
    product_handle: handle,
    product_title: title,
  });
}

export function trackLogin(): void {
  void send({ event_type: "login", path: lastPath ?? undefined });
}

export function trackSignup(): void {
  void send({ event_type: "signup", path: lastPath ?? undefined });
}

export function trackBeginCheckout(): void {
  void send({ event_type: "begin_checkout", path: lastPath ?? undefined });
}

export function trackAgentOpen(): void {
  void send({ event_type: "agent_open", path: lastPath ?? undefined });
}

/** نقرة على زرّ واتساب — تُخزَّن في customer_events ليُحسب العدّاد في لوحة الأدمن */
export function trackWhatsappClick(location: string, extra?: Record<string, string>): void {
  void send({
    event_type: "whatsapp_click",
    path: lastPath ?? undefined,
    metadata: { location, ...(extra ?? {}) },
  });
}

export function initActivityTracker(): void {
  if (typeof window === "undefined") return;
  if (trackerInited) return; // already initialized
  trackerInited = true;

  // Initial page view
  trackPageView(window.location.pathname + window.location.search);

  // Heartbeat — يعمل فقط والتبويب مرئيّ، ويتوقّف كلّياً عند الخفاء
  const startHeartbeat = () => {
    if (heartbeatTimer) return;
    heartbeatTimer = setInterval(() => {
      if (document.visibilityState === "visible") trackHeartbeat();
    }, HEARTBEAT_MS);
  };
  const stopHeartbeat = () => {
    if (!heartbeatTimer) return;
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  };
  startHeartbeat();
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") startHeartbeat();
    else stopHeartbeat();
  });


  // Final beacon on hide
  window.addEventListener("pagehide", () => {
    void send({ event_type: "page_view", path: lastPath ?? "/", heartbeat: true }, true);
  });
}
